import { useEffect, useMemo, useRef, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import DropDown from "@/components/forms/inputs/dropDown";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import {
  createPromiseRequest,
  getLmsManagerRequestConfig,
  getLmsManagerRequestParameters,
  isLmsCourseEnrolmentEligible,
  LmsMode,
} from "@/components/lms/lms.utils";
import LmsCustomIntervalFields, {
  buildCustomIntervalPayload,
  CUSTOM_INTERVAL_VALIDATION_TARGETS,
  getInvalidCustomIntervalTargets,
  getDefaultAssignmentIntervalState,
  isCustomIntervalValidationTarget,
  LmsCustomIntervalValidationTarget,
  LmsCustomIntervalState,
} from "@/components/lms/lmsCustomIntervalFields";
import styles from "@/components/lms/lmsEnrolmentCreateEditModals.module.css";
import {
  KNOWN_ASSIGNMENT_TYPES,
  getAssignmentTypeOptions,
  useEnrolmentAssignmentTargetSelection,
} from "@/components/lms/enrolmentAssignmentTargetSelection";
import { RootState } from "@/components/state/store";
import { SubjectOption } from "@/helper/customHooks/useSubjectSearch";
import { localizeLmsContractEligibilityError } from "@/components/lms/lmsQuickSearchEligibility";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";

interface Props {
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
  courseId?: string;
  enableCourseSelection: boolean;
  mode?: LmsMode;
  presetParticipants?: SubjectOption[];
  presetCompanies?: SubjectOption[];
  lockParticipantSelection?: boolean;
  excludedAssignmentTypes?: readonly string[];
}

interface CourseOption {
  value: string;
  label: string;
}

type CreateValidationTarget =
  | "courses"
  | "participantCompany"
  | "participants"
  | "companies"
  | "departmentCompany"
  | "departments"
  | "fallbackTarget"
  | LmsCustomIntervalValidationTarget;

const EMPTY_SUBJECT_OPTIONS: SubjectOption[] = [];
const EMPTY_EXCLUDED_ASSIGNMENT_TYPES: string[] = [];
const ASSIGNMENT_TYPE_DEFAULT_PRIORITY = [
  "multiple_participants",
  "multiple_companies",
  "multiple_departments",
] as const;

const pickDefaultAssignmentType = (availableTypes: readonly string[]) =>
  ASSIGNMENT_TYPE_DEFAULT_PRIORITY.find((type) =>
    availableTypes.includes(type)
  ) ??
  availableTypes[0] ??
  "multiple_participants";

export default function LmsEnrolmentCreateModal({
  open,
  onClose,
  onCompleted,
  courseId,
  enableCourseSelection = false,
  mode = "administrator",
  presetParticipants = EMPTY_SUBJECT_OPTIONS,
  presetCompanies = EMPTY_SUBJECT_OPTIONS,
  lockParticipantSelection = false,
  excludedAssignmentTypes = EMPTY_EXCLUDED_ASSIGNMENT_TYPES,
}: Props) {
  const { t } = useTranslation();
  const subdomain = window.location.hostname.split(".")[0];
  const selectedChildCompany = useSelector((state: RootState) => state.parent);
  const [selectedCourses, setSelectedCourses] = useState<CourseOption[]>([]);
  const [courseOptions, setCourseOptions] = useState<CourseOption[]>([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [assignmentTypeOptions, setAssignmentTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedAssignmentType, setSelectedAssignmentType] = useState<string>(
    "multiple_participants"
  );
  const [includeChildCompanies, setIncludeChildCompanies] = useState(false);
  const [customIntervals, setCustomIntervals] =
    useState<LmsCustomIntervalState>(getDefaultAssignmentIntervalState);
  const [validationErrors, setValidationErrors] = useState<
    CreateValidationTarget[]
  >([]);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, CreateValidationTarget>({
      fieldOrder: [],
    });
  const { handleError, clearError, errorModal } = useErrorModal(
    t("create_new_enrolments")
  );
  const {
    selectedTargetOptions,
    selectedDepartments,
    selectedDepartmentCompanies,
    selectedParticipantCompanies,
    setSelectedDepartments,
    setSelectedDepartmentCompanies,
    setSelectedParticipantCompanies,
    setSelectedParticipants,
    usesManagerCompanyScope,
    managerLockedCompanyOption,
    managerLockedCompanyDisplayName,
    handleGenericTargetChange,
    loadParticipantOptions,
    loadCompanyOptions,
    loadDepartmentOptions,
    loadAllDepartmentOptions,
    departmentOptionsScopeKey,
    loadFallbackSubjectOptions,
    getSelectionBlockedMessage,
    buildAssignmentTargetPayload,
    resetSelections,
    initializeSelectionsFromAssignment,
    applyAssignmentTypeChange,
  } = useEnrolmentAssignmentTargetSelection({
    selectedAssignmentType,
    mode,
    subdomain,
    selectedCompanySubjectId: selectedChildCompany.subjectId,
    selectedCompanyName: selectedChildCompany.companyName,
  });
  const managerRequestConfig = useMemo(
    () => getLmsManagerRequestConfig(mode, selectedChildCompany.subjectId),
    [mode, selectedChildCompany.subjectId]
  );
  const managerRequestParameters = useMemo(
    () => getLmsManagerRequestParameters(mode, selectedChildCompany.subjectId),
    [mode, selectedChildCompany.subjectId]
  );
  const presetKey = useMemo(
    () =>
      [
        ...presetCompanies.map((company) => `c:${company.value}`),
        ...presetParticipants.map((participant) => `p:${participant.value}`),
      ].join("|"),
    [presetCompanies, presetParticipants]
  );
  const initSessionRef = useRef<string | null>(null);
  const excludedAssignmentTypesKey = excludedAssignmentTypes.join(",");
  const allowedKnownAssignmentTypes = useMemo(
    () =>
      KNOWN_ASSIGNMENT_TYPES.filter(
        (type) => !excludedAssignmentTypes.includes(type)
      ),
    [excludedAssignmentTypes, excludedAssignmentTypesKey]
  );
  const defaultAssignmentType = pickDefaultAssignmentType(
    allowedKnownAssignmentTypes
  );

  const mapAssignmentTypesToOptions = (types: string[]) =>
    getAssignmentTypeOptions(t, { usesManagerCompanyScope, types });
  const filterAssignmentTypes = (types: string[]) =>
    types.filter((type) => !excludedAssignmentTypes.includes(type));

  useEffect(() => {
    if (!open) {
      initSessionRef.current = null;
      return;
    }

    const initSessionKey = [
      presetKey,
      String(enableCourseSelection),
      String(lockParticipantSelection),
      mode,
      String(selectedChildCompany.subjectId ?? ""),
      courseId ?? "",
      excludedAssignmentTypesKey,
    ].join("::");

    if (initSessionRef.current === initSessionKey) {
      return;
    }
    initSessionRef.current = initSessionKey;

    resetSelections();
    setSelectedAssignmentType(defaultAssignmentType);
    setValidationErrors([]);
    clearError();
    setSelectedCourses([]);
    setCourseOptions([]);
    setIsLoadingCourses(false);
    setIncludeChildCompanies(false);
    setCustomIntervals(getDefaultAssignmentIntervalState());

    if (presetCompanies.length > 0) {
      initializeSelectionsFromAssignment({
        company_subject_set: presetCompanies,
      });
      setSelectedAssignmentType("multiple_companies");
    } else if (
      presetParticipants.length > 0 &&
      !excludedAssignmentTypes.includes("multiple_participants")
    ) {
      initializeSelectionsFromAssignment({
        participant_set: presetParticipants,
      });
      setSelectedAssignmentType("multiple_participants");
    }

    const requestPromises: Promise<any>[] = [];

    if (enableCourseSelection) {
      setIsLoadingCourses(true);
      requestPromises.push(
        createPromiseRequest({
          ...managerRequestConfig,
          entity: "course",
          method: "get",
          getAllPages: true,
            parametersToRender: {
              is_active: true,
              ...managerRequestParameters,
              ...(mode === "manager" ? { prefer_translation: "true" } : {}),
            },
        })
          .then((response) => {
            const options = (response?.data?.results ?? [])
              .filter(isLmsCourseEnrolmentEligible)
              .map((course: any) => ({
                value: String(course.id),
                label: course.title ?? course.name ?? String(course.id),
              }));
            setCourseOptions(options);
          })
          .finally(() => {
            setIsLoadingCourses(false);
          })
      );
    }

    if (!lockParticipantSelection) {
      requestPromises.push(
        createPromiseRequest({
          ...managerRequestConfig,
          entity: "enrolmentAssignment",
          method: "get",
          additionalRouteParts: { available_types: "/" },
          parametersToRender: managerRequestParameters,
        }).then((response) => {
          const rawTypes = Array.isArray(response?.data?.results)
            ? response.data.results
            : [];
          const availableTypes = filterAssignmentTypes(
            rawTypes.length > 0 ? rawTypes : [...KNOWN_ASSIGNMENT_TYPES]
          );
          if (availableTypes.length === 0) {
            return;
          }
          const options = mapAssignmentTypesToOptions(availableTypes);
          setAssignmentTypeOptions(options);

          const preferredPresetType =
            presetCompanies.length > 0
              ? "multiple_companies"
              : presetParticipants.length > 0
              ? "multiple_participants"
              : null;

          if (
            preferredPresetType &&
            availableTypes.includes(preferredPresetType)
          ) {
            setSelectedAssignmentType(preferredPresetType);
            return;
          }

          setSelectedAssignmentType(pickDefaultAssignmentType(availableTypes));
        })
      );
    }

    Promise.all(requestPromises).catch((requestError) => {
      handleError(requestError);
    });
  }, [
    open,
    presetKey,
    enableCourseSelection,
    managerRequestConfig,
    managerRequestParameters,
    mode,
    lockParticipantSelection,
    courseId,
    selectedChildCompany.subjectId,
    excludedAssignmentTypesKey,
    defaultAssignmentType,
  ]);

  const effectiveAssignmentTypeOptions =
    assignmentTypeOptions.length > 0
      ? assignmentTypeOptions
      : mapAssignmentTypesToOptions(allowedKnownAssignmentTypes);

  const clearValidationErrors = (...targets: CreateValidationTarget[]) => {
    setValidationErrors((current) =>
      current.filter((target) => !targets.includes(target)),
    );
  };

  const getInvalidTargets = (): CreateValidationTarget[] => {
    const invalidTargets: CreateValidationTarget[] = [];
    if (enableCourseSelection && selectedCourses.length === 0) {
      invalidTargets.push("courses");
    }

    if (!lockParticipantSelection) {
      if (selectedAssignmentType === "multiple_participants") {
        if (
          !usesManagerCompanyScope &&
          selectedParticipantCompanies.length === 0
        ) {
          invalidTargets.push("participantCompany");
        }
        if (selectedTargetOptions.length === 0) {
          invalidTargets.push("participants");
        }
      } else if (selectedAssignmentType === "multiple_companies") {
        if (!usesManagerCompanyScope && selectedTargetOptions.length === 0) {
          invalidTargets.push("companies");
        }
      } else if (selectedAssignmentType === "multiple_departments") {
        if (
          !usesManagerCompanyScope &&
          selectedDepartmentCompanies.length === 0
        ) {
          invalidTargets.push("departmentCompany");
        }
        if (selectedTargetOptions.length === 0) {
          invalidTargets.push("departments");
        }
      } else if (selectedTargetOptions.length === 0) {
        invalidTargets.push("fallbackTarget");
      }
    }

    invalidTargets.push(...getInvalidCustomIntervalTargets(customIntervals));
    return invalidTargets;
  };

  const contextUnavailable =
    (!enableCourseSelection && !courseId) ||
    (lockParticipantSelection && selectedTargetOptions.length === 0) ||
    (selectedAssignmentType === "multiple_companies" &&
      usesManagerCompanyScope &&
      !managerLockedCompanyOption?.value);

  const handleCreateEnrolments = async () => {
    const invalidTargets = getInvalidTargets();
    setValidationErrors(invalidTargets);
    if (invalidTargets.length > 0) {
      focusFirstInvalidField({}, invalidTargets);
      return;
    }

    const courseIds = enableCourseSelection
      ? selectedCourses.map((course) => course.value)
      : courseId
      ? [courseId]
      : [];

    if (courseIds.length === 0 || selectedTargetOptions.length === 0) {
      return;
    }

    const assignmentTargetPayload = buildAssignmentTargetPayload();
    const intervalPayload = buildCustomIntervalPayload(customIntervals);

    try {
      await Promise.all(
        courseIds.map((id) =>
          createPromiseRequest({
            ...managerRequestConfig,
            entity: "enrolmentAssignment",
            method: "post",
            parametersToRender: managerRequestParameters,
            config: {
              headers: {
                "Content-Type": "application/json",
              },
            },
            submitData: {
              course: id,
              type: selectedAssignmentType,
              include_child_companies:
                selectedAssignmentType === "multiple_companies"
                  ? includeChildCompanies
                  : false,
              ...intervalPayload,
              ...assignmentTargetPayload,
            },
          })
        )
      );

      onCompleted();
    } catch (requestError) {
      handleError(localizeLmsContractEligibilityError(requestError, t));
    }
  };
  const { handleSubmit, isSubmitting } = useFormSubmission({
    onSubmit: handleCreateEnrolments,
    disabled: contextUnavailable || isLoadingCourses,
  });

  return (
    <>
      <Dialog
        data-testid="lms-enrolment-create-modal"
        open={open}
        onClose={() => {
          if (!isSubmitting) {
            onClose();
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className={styles.dialogTitle}>
          {enableCourseSelection ? t("assign_enrolment") : t("assign_enrolments")}
        </DialogTitle>
        <form style={{ display: "contents" }} onSubmit={handleSubmit} noValidate>
          <DialogContent className={styles.dialogContent}>
          {enableCourseSelection && (
            <div
              data-testid="lms-enrolment-course-select"
              ref={registerValidationTarget("courses")}>
              <DropDown
                options={courseOptions}
                isMulti={true}
                isClearable={true}
                externalValue={selectedCourses}
                onChange={(value) => {
                  clearValidationErrors("courses");
                  setSelectedCourses(
                    Array.isArray(value) ? value : value ? [value] : [],
                  );
                }}
                label={t("courses")}
                mandatory={true}
                name="global-enrolment-course"
                showLoadingIndicator={isLoadingCourses}
              />
              {validationErrors.includes("courses") && (
                <Typography color="error" variant="caption" role="alert">
                  {t("field_required")}
                </Typography>
              )}
            </div>
          )}
          {!lockParticipantSelection && (
            <>
              <div data-testid="lms-enrolment-target-type-select">
                <DropDown
                  options={effectiveAssignmentTypeOptions}
                  isClearable={false}
                  externalValue={
                    effectiveAssignmentTypeOptions.find(
                      (option) => option.value === selectedAssignmentType
                    ) ?? effectiveAssignmentTypeOptions[0]
                  }
                  onChange={(value: any) => {
                    const nextType =
                      value?.value && typeof value.value === "string"
                        ? value.value
                        : defaultAssignmentType;
                    if (nextType !== selectedAssignmentType) {
                      applyAssignmentTypeChange(
                        selectedAssignmentType,
                        nextType
                      );
                      setIncludeChildCompanies(false);
                    }
                    setSelectedAssignmentType(nextType);
                  }}
                  label={t("type")}
                  mandatory={true}
                  name="global-enrolment-target-type"
                />
              </div>
              {selectedAssignmentType === "multiple_participants" && (
                <>
                  {!usesManagerCompanyScope && (
                    <div
                      data-testid="lms-enrolment-participant-company-select"
                      ref={registerValidationTarget("participantCompany")}>
                      <FormFieldLabel
                        label={t("company")}
                        htmlFor="lms-enrolment-participant-company"
                        mandatory
                      />
                      <SubjectSearchSelect
                        name="lms-enrolment-participant-company"
                        isMulti={false}
                        value={selectedParticipantCompanies[0] ?? null}
                        loadOptions={loadCompanyOptions}
                        onChange={(value: any) => {
                          clearValidationErrors(
                            "participantCompany",
                            "participants",
                          );
                          if (!value) {
                            setSelectedParticipantCompanies([]);
                            setSelectedParticipants([]);
                            return;
                          }
                          setSelectedParticipantCompanies([value]);
                          setSelectedParticipants([]);
                        }}
                        isClearable={true}
                        placeholder={t("search_placeholder")}
                        getSelectionBlockedMessage={getSelectionBlockedMessage}
                      />
                      {validationErrors.includes("participantCompany") && (
                        <Typography
                          color="error"
                          variant="caption"
                          role="alert">
                          {t("field_required")}
                        </Typography>
                      )}
                    </div>
                  )}
                  <div
                    data-testid="lms-enrolment-participants-select"
                    ref={registerValidationTarget("participants")}>
                    <FormFieldLabel
                      label={t("persons")}
                      htmlFor="lms-enrolment-participants"
                      mandatory
                    />
                    <SubjectSearchSelect
                      name="lms-enrolment-participants"
                      isMulti={true}
                      value={selectedTargetOptions}
                      loadOptions={loadParticipantOptions}
                      onChange={(value: any) => {
                        clearValidationErrors("participants");
                        handleGenericTargetChange(value);
                      }}
                      isClearable={true}
                      isDisabled={
                        !usesManagerCompanyScope &&
                        selectedParticipantCompanies.length === 0
                      }
                      placeholder={t("search_placeholder")}
                      getSelectionBlockedMessage={getSelectionBlockedMessage}
                    />
                    {validationErrors.includes("participants") && (
                      <Typography color="error" variant="caption" role="alert">
                        {t("field_required")}
                      </Typography>
                    )}
                  </div>
                </>
              )}
              {selectedAssignmentType === "multiple_companies" &&
                (usesManagerCompanyScope ? (
                  <DefaultInput
                    label={t("company")}
                    value={managerLockedCompanyDisplayName}
                    readonly={true}
                    mandatory={true}
                    disabled={true}
                  />
                ) : (
                  <div
                    data-testid="lms-enrolment-companies-select"
                    ref={registerValidationTarget("companies")}>
                    <FormFieldLabel
                      label={t("companies")}
                      htmlFor="lms-enrolment-companies"
                      mandatory
                    />
                    <SubjectSearchSelect
                      name="lms-enrolment-companies"
                      isMulti={true}
                      value={selectedTargetOptions}
                      loadOptions={loadCompanyOptions}
                      onChange={(value: any) => {
                        clearValidationErrors("companies");
                        handleGenericTargetChange(value);
                      }}
                      isClearable={true}
                      placeholder={t("search_placeholder")}
                      getSelectionBlockedMessage={getSelectionBlockedMessage}
                    />
                    {validationErrors.includes("companies") && (
                      <Typography color="error" variant="caption" role="alert">
                        {t("field_required")}
                      </Typography>
                    )}
                  </div>
                ))}
              {selectedAssignmentType === "multiple_companies" && (
                <FormControlLabel
                  label={t("include_child_companies")}
                  control={
                    <Checkbox
                      checked={includeChildCompanies}
                      onChange={(event) =>
                        setIncludeChildCompanies(event.target.checked)
                      }
                      color="secondary"
                    />
                  }
                />
              )}
              {selectedAssignmentType !== "multiple_departments" &&
                selectedAssignmentType !== "multiple_participants" &&
                selectedAssignmentType !== "multiple_companies" && (
                  <div
                    data-testid="lms-enrolment-fallback-target-select"
                    ref={registerValidationTarget("fallbackTarget")}>
                    <FormFieldLabel
                      label={t("subject")}
                      htmlFor="lms-enrolment-subject"
                      mandatory
                    />
                    <SubjectSearchSelect
                      name="lms-enrolment-subject"
                      isMulti={true}
                      value={selectedTargetOptions}
                      loadOptions={loadFallbackSubjectOptions}
                    onChange={(value: any) => {
                      clearValidationErrors("fallbackTarget");
                      handleGenericTargetChange(value);
                    }}
                      isClearable={true}
                      placeholder={t("search_placeholder")}
                      getSelectionBlockedMessage={getSelectionBlockedMessage}
                  />
                  {validationErrors.includes("fallbackTarget") && (
                    <Typography color="error" variant="caption" role="alert">
                      {t("field_required")}
                    </Typography>
                  )}
                </div>
                )}
              {selectedAssignmentType === "multiple_departments" && (
                <>
                  {!usesManagerCompanyScope && (
                    <div
                      data-testid="lms-enrolment-department-company-select"
                      ref={registerValidationTarget("departmentCompany")}>
                      <FormFieldLabel
                        label={t("company")}
                        htmlFor="lms-enrolment-department-company"
                        mandatory
                      />
                      <SubjectSearchSelect
                        name="lms-enrolment-department-company"
                        isMulti={false}
                        value={selectedDepartmentCompanies[0] ?? null}
                        loadOptions={loadCompanyOptions}
                        onChange={(value: any) => {
                          clearValidationErrors(
                            "departmentCompany",
                            "departments",
                          );
                          if (!value) {
                            setSelectedDepartmentCompanies([]);
                            setSelectedDepartments([]);
                            return;
                          }
                          setSelectedDepartmentCompanies([value]);
                          setSelectedDepartments([]);
                        }}
                        isClearable={true}
                        placeholder={t("search_placeholder")}
                        getSelectionBlockedMessage={getSelectionBlockedMessage}
                      />
                      {validationErrors.includes("departmentCompany") && (
                        <Typography
                          color="error"
                          variant="caption"
                          role="alert">
                          {t("field_required")}
                        </Typography>
                      )}
                    </div>
                  )}
                  <div
                    data-testid="lms-enrolment-departments-select"
                    ref={registerValidationTarget("departments")}>
                    <FormFieldLabel
                      label={t("departments")}
                      htmlFor="lms-enrolment-departments"
                      mandatory
                    />
                    <SubjectSearchSelect
                      name="lms-enrolment-departments"
                      isMulti={true}
                      value={selectedDepartments}
                      loadOptions={loadDepartmentOptions}
                      loadAllOptions={loadAllDepartmentOptions}
                      loadAllOptionsResetKey={departmentOptionsScopeKey}
                      onLoadAllOptionsError={handleError}
                      onChange={(value: any) => {
                        clearValidationErrors("departments");
                        setSelectedDepartments(
                          Array.isArray(value) ? value : [],
                        );
                      }}
                      isClearable={true}
                      isDisabled={
                        !usesManagerCompanyScope &&
                        selectedDepartmentCompanies.length === 0
                      }
                      placeholder={t("search_placeholder")}
                      getSelectionBlockedMessage={getSelectionBlockedMessage}
                    />
                    {validationErrors.includes("departments") && (
                      <Typography color="error" variant="caption" role="alert">
                        {t("field_required")}
                      </Typography>
                    )}
                  </div>
                </>
              )}
            </>
          )}
          <LmsCustomIntervalFields
            value={customIntervals}
            onChange={(value) => {
              clearValidationErrors(...CUSTOM_INTERVAL_VALIDATION_TARGETS);
              setCustomIntervals(value);
            }}
            invalidTargets={validationErrors.filter(
              isCustomIntervalValidationTarget,
            )}
            registerCompletionTarget={registerValidationTarget(
              "completionInterval",
            )}
            registerValidityTarget={registerValidationTarget(
              "validityInterval",
            )}
            registerRenewalCooldownTarget={registerValidationTarget(
              "renewalCooldown",
            )}
          />
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
          <GenericButton
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("cancel")}
          </GenericButton>
          <GenericButton
            type="submit"
            data-testid="lms-enrolment-submit-button"
            disabled={
              isSubmitting ||
              isLoadingCourses ||
              contextUnavailable
            }
          >
            {t("enrol")}
          </GenericButton>
          </DialogActions>
        </form>
      </Dialog>

      {errorModal}
    </>
  );
}
